import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { listMembers, removeMember, updateProfessionalProfile, updateProfessionalServices } from "../../services/memberService";
import api from "../../services/api";
import { ButtonBack, ButtonSave } from "../../components/ui";
import styles from "./Professionals.module.css";

const SPECIALTY_OPTIONS = [
  "Cabeleireiro(a)",
  "Manicure / Pedicure",
  "Esteticista",
  "Barbeiro(a)",
  "Maquiador(a)",
  "Massagista",
  "Depilador(a)",
  "Designer de Sobrancelhas",
  "Terapeuta",
  "Outro",
];

function Avatar({ name, avatarUrl, size = 44 }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }}
      />
    );
  }
  const initial = name ? name.charAt(0).toUpperCase() : "?";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: "#6e0ad4",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: size * 0.4,
        flexShrink: 0,
      }}
    >
      {initial}
    </div>
  );
}

function EditModal({ member, onClose, onSaved }) {
  const [name, setName] = useState(member.user?.name ?? "");
  const [email, setEmail] = useState(member.user?.email ?? "");
  const [specialty, setSpecialty] = useState(member.specialty ?? "");
  const [bio, setBio] = useState(member.bio ?? "");
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState(
    (member.professionalServices ?? []).map((ps) => ps.serviceId).filter(Boolean),
  );
  const overlayRef = useRef();

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  // Close on backdrop click
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSave = async () => {
    // Validação client-side básica
    if (!name.trim() || name.trim().length < 2) {
      toast.error("Nome deve ter ao menos 2 caracteres.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error("E-mail inválido.");
      return;
    }

    setSaving(true);
    const [profileResult, servicesResult] = await Promise.allSettled([
      updateProfessionalProfile(member.userId, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        specialty: specialty || null,
        bio: bio || null,
      }),
      updateProfessionalServices(member.userId, selectedServiceIds),
    ]);
    setSaving(false);

    const profileOk = profileResult.status === "fulfilled";
    const servicesOk = servicesResult.status === "fulfilled";

    if (!profileOk) {
      toast.error("Erro ao salvar perfil.");
      return;
    }

    if (!servicesOk) {
      toast.warning("Perfil salvo, mas falha ao atualizar serviços. Tente novamente.");
    } else {
      toast.success("Perfil atualizado com sucesso!");
    }

    onSaved(profileResult.value.data);
    onClose();
  };

  return (
    <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar name={member.user?.name} avatarUrl={member.user?.avatarUrl} size={40} />
            <div>
              <p className={styles.modalName}>{member.user?.name ?? "—"}</p>
              <p className={styles.modalEmail}>{member.user?.email}</p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Fechar">✕</button>
        </div>

        <div className={styles.modalBody}>
          <label className={styles.fieldLabel}>
            Nome
            <input
              className={styles.select}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Nome completo"
            />
          </label>

          <label className={styles.fieldLabel}>
            E-mail
            <input
              className={styles.select}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              maxLength={200}
              placeholder="email@exemplo.com"
            />
          </label>

          <label className={styles.fieldLabel}>
            Especialidade
            <select
              className={styles.select}
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
            >
              <option value="">— Selecione —</option>
              {SPECIALTY_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>

          <label className={styles.fieldLabel}>
            Apresentação (bio)
            <textarea
              className={styles.textarea}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="Breve descrição sobre o profissional..."
            />
            <span className={styles.charCount}>{bio.length}/500</span>
          </label>

          {services.length > 0 && (
            <div>
              <p className={styles.fieldLabel} style={{ marginBottom: "0.5rem" }}>
                Serviços oferecidos
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                {services.map((svc) => {
                  const checked = selectedServiceIds.includes(svc.id);
                  return (
                    <label
                      key={svc.id}
                      style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.875rem" }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelectedServiceIds((prev) =>
                            checked ? prev.filter((id) => id !== svc.id) : [...prev, svc.id],
                          )
                        }
                      />
                      {svc.name} ({svc.duration} min)
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <ButtonSave onClick={handleSave} isLoading={saving}>
            Salvar
          </ButtonSave>
        </div>
      </div>
    </div>
  );
}

export default function Professionals() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const currentMemberRole = useMemo(
    () => members.find((m) => m.userId === user?.id)?.role ?? null,
    [members, user?.id],
  );

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listMembers();
      setMembers(res.data);
    } catch {
      toast.error("Erro ao carregar profissionais.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSaved = (updated) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m))
    );
  };

  const handleDelete = async (userId) => {
    setDeleting(true);
    try {
      await removeMember(userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
      setDeletingMemberId(null);
      toast.success("Profissional removido com sucesso.");
    } catch (e) {
      toast.error(e?.response?.data?.error ?? "Erro ao remover profissional.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <ButtonBack to="/agenda" style={{ marginBottom: 8 }} />
        <div>
          <h1 className={styles.title}>Profissionais</h1>
          <p className={styles.subtitle}>Gerencie os perfis dos membros da sua equipe</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className={styles.newBtn} onClick={() => navigate("/business/professionals/new")}>
            ＋ Novo Profissional
          </button>
          <button className={styles.hoursBtn} onClick={() => navigate("/business/professionals/hours")}>
            🗓 Gerenciar Expedientes
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Carregando profissionais...</div>
      ) : members.length === 0 ? (
        <div className={styles.empty}>
          <p>Nenhum membro encontrado.</p>
          <p>Convide membros pela página <strong>Equipe</strong>.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {members.map((m) => (
            <div key={m.id} className={styles.card}>
              <div className={styles.cardTop}>
                <Avatar name={m.user?.name} avatarUrl={m.user?.avatarUrl} />
                <div className={styles.cardInfo}>
                  <p className={styles.cardName}>{m.user?.name ?? "—"}</p>
                  <p className={styles.cardEmail}>{m.user?.email}</p>
                </div>
              </div>

              <div className={styles.cardBody}>
                {m.specialty ? (
                  <span className={styles.specialtyTag}>{m.specialty}</span>
                ) : (
                  <span className={styles.noSpecialty}>Sem especialidade definida</span>
                )}
                {m.bio && (
                  <p className={styles.bio}>{m.bio}</p>
                )}
              </div>

              <div className={styles.cardFooter}>
                <span className={`${styles.roleBadge} ${styles[`role${m.role}`]}`}>
                  {m.role === "OWNER" ? "Proprietário" : m.role === "ADMIN" ? "Admin" : "Membro"}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    className={styles.editBtn}
                    onClick={() => setEditingMember(m)}
                  >
                    Editar perfil
                  </button>
                  {m.role !== "OWNER" && currentMemberRole !== "MEMBER" && (
                    deletingMemberId === m.userId ? (
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>Confirmar?</span>
                        <button
                          style={{ fontSize: "0.75rem", padding: "4px 10px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
                          onClick={() => handleDelete(m.userId)}
                          disabled={deleting}
                        >
                          {deleting ? "..." : "Sim"}
                        </button>
                        <button
                          style={{ fontSize: "0.75rem", padding: "4px 10px", background: "#f0f0f0", color: "#333", border: "none", borderRadius: 6, cursor: "pointer" }}
                          onClick={() => setDeletingMemberId(null)}
                          disabled={deleting}
                        >
                          Não
                        </button>
                      </div>
                    ) : (
                      <button
                        style={{ fontSize: "0.75rem", padding: "4px 12px", background: "none", color: "#ef4444", border: "1px solid #ef4444", borderRadius: 6, cursor: "pointer" }}
                        onClick={() => setDeletingMemberId(m.userId)}
                      >
                        Excluir
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingMember && (
        <EditModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
